import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Link as MuiLink,
  IconButton,
  useTheme,
  Tooltip,
} from '@mui/material';
import { OrderItem } from 'api/ordering-service/ordering-service.types';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { formatCurrency } from 'util/currency';
import { ArrowForward } from '@mui/icons-material';

type OrderItemCardProps = {
  item: OrderItem;
  isModifier?: boolean;
};
const OrderItemCard = ({ item, isModifier = false }: OrderItemCardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const [isCopiedId, setIdCopied] = useState(false);
  const [isCopiedExtId, setExtIdCopied] = useState(false);
  return (
    <Box
      sx={{ ml: isModifier ? 2 : 0, p: 1, pl: 0 }}
      data-testid={'OrderItemCard'}
    >
      <Card
        sx={{
          minWidth: 275,
          minHeight: 180,
          borderLeft: isModifier ? '3px solid #42A5F5' : '3px solid #00426A',
        }}
      >
        <CardContent>
          <Grid container direction="row" pt={2} alignItems={'center'}>
            <Grid size={{ xs: 8 }}>
              <Typography variant="h5">{item.displayInfoTitle}</Typography>
            </Grid>
            <Box
              display={'flex'}
              sx={{ flex: 1 }}
              justifyContent="space-between"
            >
              <Typography variant="subtitle2" sx={{ fontWeight: '700' }}>
                {item.quantity}
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: '700' }}>
                {formatCurrency(parseFloat(item.itemTotal.total), 'USD')}
              </Typography>
            </Box>
          </Grid>
          <Box
            display={'flex'}
            sx={{ flex: 1, pb: 1, minHeight: 18 }}
            justifyContent="end"
          >
            {item?.itemDiscounts?.map((discount, idx) => {
              return (
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    fontSize: '12px',
                  }}
                >
                  {t('drawer.orderDetailsDrawer.discount', {
                    discount: discount?.displayAppliedDiscount,
                  })}
                </Typography>
              );
            })}
          </Box>
          <Box display={'flex'} justifyContent="start" alignItems={'center'}>
            <Typography
              variant="subtitle2"
              sx={{ wordBreak: 'break-all' }}
              fontSize={12}
              fontWeight={'700'}
              mr={0.5}
            >
              {t('OrderItemCard.ID.label')}
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{ wordBreak: 'break-all' }}
              fontSize={12}
            >
              {item.itemId}
            </Typography>
            <Tooltip title={isCopiedId ? 'Copied' : 'Copy'}>
              <IconButton
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: theme.palette.primary.main,
                }}
                onClick={() => {
                  navigator.clipboard.writeText(item.itemId);
                  setIdCopied(true);
                }}
                onMouseLeave={() => {
                  setIdCopied(false);
                }}
                data-testid={'OrderItemCard.copy.id'}
                aria-label="copy"
              >
                <ContentCopyIcon sx={{ fontSize: 14, cursor: 'pointer' }} />
              </IconButton>
            </Tooltip>
          </Box>
          {item.externalId && (
            <Box display={'flex'} justifyContent="start" alignItems={'center'}>
              <Typography
                variant="subtitle2"
                sx={{ wordBreak: 'break-all' }}
                fontSize={12}
                fontWeight={'700'}
                mr={0.5}
              >
                {t('OrderItemCard.ExtID.label')}
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{ wordBreak: 'break-all' }}
                fontSize={12}
              >
                {item.externalId}
              </Typography>
              <Tooltip title={isCopiedExtId ? 'Copied' : 'Copy'}>
                <IconButton
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: theme.palette.primary.main,
                  }}
                  onClick={() => {
                    navigator.clipboard.writeText(item.externalId);
                    setExtIdCopied(true);
                  }}
                  onMouseLeave={() => {
                    setExtIdCopied(false);
                  }}
                  data-testid={'OrderItemCard.copy.extid'}
                  aria-label="copy"
                >
                  <ContentCopyIcon sx={{ fontSize: 14, cursor: 'pointer' }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          <Box display={'flex'} justifyContent="end" pt={2}>
            <MuiLink
              href="#"
              underline="always"
              sx={{ display: 'flex', alignItems: 'flex-end' }}
              onClick={() => navigate(`#`)}
            >
              <Typography>{t('OrderItemCard.GoToItem.label')}</Typography>
              <ArrowForward sx={{ mr: 1, ml: 1 }} fontSize="small" />
            </MuiLink>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default OrderItemCard;
